import React, { useState, useRef, useEffect, useCallback } from "react";

const COMMANDS = ["help", "whoami", "ls", "cat", "cd", "clear", "neofetch"];
const DIRS = ["about/", "experience/", "skills/", "projects/", "achievements/", "contact/"];
const CAT_FILES = ["skills.txt", "experience.txt", "bio.txt"];

const SECTION_MAP = {
  about: "about",
  experience: "experience",
  skills: "resume",
  projects: "projects",
  achievements: "certificate",
  contact: "contact",
};

const NEOFETCH = [
  { type: "highlight", text: "         vivek@portfolio" },
  { type: "output",    text: "         ─────────────────" },
  { type: "output",    text: "   ██╗   OS:     React 18 / Tokyo Night" },
  { type: "output",    text: "   ██║   Host:   Vivek Singh" },
  { type: "output",    text: "   ██║   Role:   SDE @ ShyftLabs" },
  { type: "output",    text: "   ██║   Stack:  Go, Python, TS, AWS" },
  { type: "output",    text: "   ██║   AI:     LangChain, RAG, LLM" },
  { type: "output",    text: "   ╚═╝   Shell:  portfolio-cli v1.0" },
];

const buildHelp = () => [
  { type: "highlight", text: "Available commands:" },
  { type: "output", text: "" },
  { type: "output", text: "  whoami          About me" },
  { type: "output", text: "  ls              List sections" },
  { type: "output", text: "  cat <file>      Read skills.txt, experience.txt, bio.txt" },
  { type: "output", text: "  cd <section>    Navigate to a section" },
  { type: "output", text: "  neofetch        System info" },
  { type: "output", text: "  clear           Clear terminal" },
  { type: "output", text: "  help            Show this message" },
];

const buildLs = () => [
  { type: "output", text: DIRS.map(d => `  📁 ${d}`).join("\n") },
  { type: "output", text: "  📄 skills.txt   📄 experience.txt   📄 bio.txt" },
];

const buildWhoami = (data) => [
  { type: "highlight", text: data?.name || "Vivek Singh" },
  { type: "output", text: data?.occupation || "Software Development Engineer" },
  { type: "output", text: data?.description || "" },
];

const buildCatSkills = (skills) => {
  if (!skills?.length) return [{ type: "output", text: "skills.txt: No data loaded yet." }];
  return skills.map(s => ({ type: "output", text: `  ${s.name.padEnd(30)} ${s.level}` }));
};

const buildCatExperience = (work) => {
  if (!work?.length) return [{ type: "output", text: "experience.txt: No data loaded yet." }];
  const lines = [];
  work.forEach(w => {
    lines.push({ type: "highlight", text: `${w.title} @ ${w.company}` });
    lines.push({ type: "output", text: `  ${w.years}  |  ${w.location}` });
    lines.push({ type: "output", text: "" });
  });
  return lines;
};

const buildCatBio = (data) => [
  { type: "output", text: data?.bio || "bio.txt: No data loaded yet." },
];

const Terminal = ({ mainData, resumeData }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [history, setHistory] = useState([
    { type: "output", text: "Welcome to vivek's portfolio terminal." },
    { type: "output", text: 'Type "help" for available commands.\n' },
  ]);
  const [input, setInput] = useState("");
  const [cmdHistory, setCmdHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [suggestion, setSuggestion] = useState("");
  const inputRef = useRef(null);
  const bodyRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, []);

  useEffect(scrollToBottom, [history, scrollToBottom]);

  const getSuggestion = useCallback((val) => {
    if (!val) return "";
    const parts = val.split(" ");
    const last = parts[parts.length - 1];
    if (!last) return "";

    if (parts.length === 1) {
      const match = COMMANDS.find(c => c.startsWith(last) && c !== last);
      return match ? match.slice(last.length) : "";
    }
    if (parts[0] === "cat") {
      const match = CAT_FILES.find(f => f.startsWith(last) && f !== last);
      return match ? match.slice(last.length) : "";
    }
    if (parts[0] === "cd") {
      const match = DIRS.map(d => d.replace("/", "")).find(d => d.startsWith(last) && d !== last);
      return match ? match.slice(last.length) : "";
    }
    return "";
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setInput(val);
    setSuggestion(getSuggestion(val));
    setHistIdx(-1);
  };

  const execCommand = useCallback((raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    const promptLine = { type: "prompt-line", text: trimmed };
    const args = trimmed.split(/\s+/);
    const cmd = args[0].toLowerCase();
    let output = [];

    switch (cmd) {
      case "help":
        output = buildHelp();
        break;
      case "whoami":
        output = buildWhoami(mainData);
        break;
      case "ls":
        output = buildLs();
        break;
      case "cat": {
        const file = args[1];
        if (!file) {
          output = [{ type: "accent", text: "Usage: cat <filename>" }];
        } else if (file === "skills.txt") {
          output = buildCatSkills(resumeData?.skills);
        } else if (file === "experience.txt") {
          output = buildCatExperience(resumeData?.work);
        } else if (file === "bio.txt") {
          output = buildCatBio(mainData);
        } else {
          output = [{ type: "accent", text: `cat: ${file}: No such file` }];
        }
        break;
      }
      case "cd": {
        const dir = args[1]?.replace("/", "");
        const sectionId = SECTION_MAP[dir];
        if (!dir) {
          output = [{ type: "accent", text: "Usage: cd <section>" }];
        } else if (sectionId) {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: "smooth" });
          output = [{ type: "output", text: `Navigating to ${dir}/...` }];
        } else {
          output = [{ type: "accent", text: `cd: ${dir}: No such directory` }];
        }
        break;
      }
      case "neofetch":
        output = NEOFETCH;
        break;
      case "clear":
        setHistory([]);
        return;
      default:
        output = [{ type: "accent", text: `command not found: ${cmd}. Type "help" for commands.` }];
    }

    setHistory(prev => [...prev, promptLine, ...output]);
  }, [mainData, resumeData]);

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      if (suggestion) {
        setInput(input + suggestion);
        setSuggestion("");
      }
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      execCommand(input);
      setCmdHistory(prev => [input, ...prev]);
      setInput("");
      setSuggestion("");
      setHistIdx(-1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (cmdHistory.length) {
        const next = Math.min(histIdx + 1, cmdHistory.length - 1);
        setHistIdx(next);
        setInput(cmdHistory[next]);
        setSuggestion("");
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (histIdx > 0) {
        const next = histIdx - 1;
        setHistIdx(next);
        setInput(cmdHistory[next]);
      } else {
        setHistIdx(-1);
        setInput("");
      }
      setSuggestion("");
    }
  };

  const focusInput = () => {
    if (!collapsed) inputRef.current?.focus();
  };

  return (
    <div className={`terminal-hero ${collapsed ? "collapsed" : ""}`} onClick={focusInput}>
      <div className="terminal-titlebar" onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}>
        <span className="terminal-dot red" />
        <span className="terminal-dot yellow" />
        <span className="terminal-dot green" />
        <span className="terminal-title">vivek@portfolio — zsh</span>
        <span className={`terminal-toggle ${collapsed ? "" : "expanded"}`}>▲</span>
      </div>
      <div className="terminal-body" ref={bodyRef}>
        {history.map((line, i) => {
          if (line.type === "prompt-line") {
            return (
              <div className="terminal-line" key={i}>
                <span className="prompt">❯ </span>
                <span className="cmd">{line.text}</span>
              </div>
            );
          }
          return (
            <div className="terminal-line" key={i}>
              <span className={line.type}>{line.text}</span>
            </div>
          );
        })}
        <div className="terminal-input-line">
          <span className="prompt">❯ </span>
          <span style={{ position: "relative", flex: 1 }}>
            <input
              ref={inputRef}
              className="terminal-input"
              value={input}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              autoComplete="off"
            />
            {suggestion && (
              <span style={{
                position: "absolute",
                left: `${input.length}ch`,
                top: 0,
                color: "var(--im-text-dim)",
                opacity: 0.4,
                pointerEvents: "none",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "13px",
              }}>
                {suggestion}
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Terminal;
